import React from "react";
import { ScrollView } from "react-native";
import PropTypes from "prop-types";

/**
 * View that automatically scrolls its content in a specific interval.
 *
 * Based on: https://medium.com/4thought-studios/react-native-auto-scrolling-for-fun-and-profit-fabadc3702e7
 * 
 * Props:
 * - interval: How often the view should scroll, in milliseconds. Defaults to 2500 milliseconds
 */
class AutoScrollView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      scrollProgress: 0,
      currentPosition: 0,
      direction: 1,
      height: 0,
      contentHeight: 0
    };

    this.onLayout = this.onLayout.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.doScroll = this.doScroll.bind(this);
  }

  /**
   * Activates the interval that does the actual scrolling.
   */
  componentDidMount() {
    this.activeInterval = setInterval(this.doScroll, this.props.interval);
  }

  /**
   * Clear the scrolling interval to deactivate the scroller.
   */
  componentWillUnmount() {
    clearInterval(this.activeInterval);
  }

  /**
   * Fired when the outer component is resized, we use it to determine the height of the verifier attributes (without the scroller).
   */
  onLayout({ nativeEvent: { layout: { height } } }) {
    this.setState({ height });
  }

  /** 
   * Fired when the outer scroller is scrolled (either automatically or by the user).
   */
  onScroll({ nativeEvent: { contentOffset: { y } } }) {
    this.setState({
      scrollProgress: Math.abs(
        y /
          (this.state.height - this.state.contentHeight)
      )
    });
  }

  /**
   * Automatically scrolls the view with an animation.
   */
  doScroll() {
    // Increment position with each new interval
    const position = this.state.currentPosition + 50 * this.state.direction;

    this.ticker.scrollTo({ x: 0, y: position, animated: true });

    // Set animation to repeat at end of scroll then back to start of scroll
    if (this.state.scrollProgress > 0.99) {
      this.setState({ direction: -1, currentPosition: position });
    } else if (this.state.currentPosition < 0) {
      this.setState({ direction: 1, currentPosition: position });
    } else {
      this.setState({ currentPosition: position });
    }
  }

  render() {
    const { children } = this.props;

    return (
      <ScrollView
        onLayout={this.onLayout}
        ref={ref => {
          this.ticker = ref;
        }}
        onContentSizeChange={(w, contentHeight) => {
          this.setState({ contentHeight });
        }}
        onScroll={this.onScroll}
        // onScroll requires this parameter be more than 16:
        scrollEventThrottle={20}
      >
        {children}
      </ScrollView>
    );
  }
}

AutoScrollView.propTypes = {
  inverval: PropTypes.number,
  children: PropTypes.node.isRequired
};

AutoScrollView.defaultProps = {
  // 2500 = 2.5 seconds
  interval: 2500
};

export default AutoScrollView;
